// Externals
import React, { useEffect, useState } from 'react';
import {
  func, node, shape, string, arrayOf,
} from 'prop-types';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { connectAsync } from 'iguazu';
import { queryModule } from 'iguazu-holocron';
import { ApolloProvider } from '@apollo/client';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { withRouter } from '@americanexpress/one-app-router';
import { Contentsquare } from '@americanexpress/shared-scripts';
import { withDayJSLocale } from '@americanexpress/dayjs-react/dist';
import { ExperimentProvider } from '@americanexpress/one-xp-client-sdk';

// Components
import HelmetConfig from '../HelmetConfig';
import DelayRender from '../DelayRender';
import ErrorReporter from '../ErrorReporter';
import QualtricsConfiguration from '../QualtricsConfiguration';
import UniversalSessionManager from '../UniversalSessionManager';
import childRoutes from '../../childRoutes';
import PreloadStylesheet from '../PreloadStylesheet';

// Ducks
import { getBrowserDetails, getConfigDetails, getStylingDetails } from '../../selectors';
import reducer from '../../ducks';

// Hooks
import { useOnTenancyChanges } from '../../hooks';

// Utils
import { isContentSquareAllowedToLoad } from '../../utils/rampUp';
import runIntlPolyfill from '../../utils/intlPolyfill';
import { getAdobeDelayRenderTime, formatLocaleForClickToChatConfig } from '../../utils/helpers';
import loadModuleData from '../../utils/loadModuleData';
import preload from '../../utils/preload';
import { queryCustomIntlData } from '../../utils/loadCustomLocaleData';
import { DLS6_VORTEX_LOCALE, generateBodyAttributes } from '../../utils/vortexAllowList';

// Configs
import {
  moduleName, customLocales, clickToChat, styleSheetHref,
} from '../../constants';
import graphClient from '../../graphConfig';
import './styles.scss';

// TODO: Define a new env variable for machine learning API domains
const mlResource = 'https://mycaoneslinger.americanexpress.com/';

const Container = ({
  children,
  languageData,
  isLoading,
  loadedWithErrors,
  routes,
  location,
  MycaVoc,
  location: { pathname },
}) => {
  const {
    docUploadApiResource,
    faasApiResource,
    graphqlApiResource,
    localeFilename,
    oneIdentity,
  } = useSelector(getConfigDetails);
  const { blueboxpublic, originLocation } = useSelector(getBrowserDetails);
  const { isDls7Enabled, isDls7Eligible, isVortexEnabled } = useSelector(getStylingDetails);
  const locale = useSelector((state) => state.getIn(['intl', 'activeLocale']));
  const [client, setClient] = useState(graphClient(graphqlApiResource, pathname, localeFilename));
  const dispatch = useDispatch();

  useEffect(() => { runIntlPolyfill(); }, []);

  useOnTenancyChanges({
    graphqlApiResource,
    locale,
    setClient,
    graphClient,
    dispatch,
  });

  const preLoadLinks = preload([
    graphqlApiResource,
    docUploadApiResource,
    faasApiResource,
    mlResource,
  ]);

  const { bodyAttributes, styleHref, colorTheme } = generateBodyAttributes({
    location,
    locale,
    styleSheetHref,
    isDls7Eligible,
    isDls7Enabled,
    isVortexEnabled,
  });

  const ctcRoute = Object.keys(clickToChat).filter((k) => pathname.startsWith(k))[0];
  const ctcLob = ctcRoute ? clickToChat[ctcRoute] : clickToChat.default;

  if (isLoading()) return <div>Loading...</div>;
  if (loadedWithErrors()) return <div>Error while loading header</div>;

  return (
    <ErrorReporter dispatch={dispatch} publicGuid={blueboxpublic}>
      <HelmetConfig
        titleTemplate={languageData?.titleTemplate}
        defaultTitle={languageData?.defaultTitle}
        locale={locale}
        routes={routes}
        preLoadLinks={preLoadLinks}
        bodyAttributes={bodyAttributes}
        styleHref={styleHref}
        colorTheme={colorTheme}
      />
      {DLS6_VORTEX_LOCALE.includes(locale) && isContentSquareAllowedToLoad() && (<Contentsquare version="^1.0.2" />)}
      <DelayRender
        config={{
          name: 'one-stream-data-handler',
          version: '^0.1.1',
          delay: '5000',
          windowCheck: 'oneStreamData',
        }}
      />
      <DelayRender
        config={{
          name: 'adobe',
          version: '^1.2.0',
          delay: getAdobeDelayRenderTime(pathname),
          isMycaWebLoyalty: true,
        }}
      />
      <DelayRender
        config={{
          name: 'qualtrics',
          version: '^1.34.0',
          delay: '13000',
          zoneId: 'ZN_dhZtUGWqHlUlqhT',
          sampleType: 'pageview',
          sampleRate: 100,
        }}
      />
      <DelayRender config={{ component: MycaVoc, delay: '13000' }} />
      <DelayRender
        config={{
          name: 'clickToChat',
          version: '^1.0.0',
          delay: '14000',
          locale,
          lineOfBusiness: ctcLob,
          market: formatLocaleForClickToChatConfig(locale),
        }}
      />
      <DelayRender
        config={{
          delay: '5000',
          component: PreloadStylesheet,
          locale,
          isVortexEnabled,
          styleHref,
          styleSheetHref,
        }}
      />
      <UniversalSessionManager
        locale={locale}
        oneIdentity={oneIdentity}
        pathname={pathname}
        originLocation={originLocation}
      />
      <QualtricsConfiguration />
      <ExperimentProvider>
        <ApolloProvider client={client}>
          {children}
        </ApolloProvider>
      </ExperimentProvider>
    </ErrorReporter>
  );
};

Container.childRoutes = childRoutes;

/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- global require
  Container.appConfig = require('../../appConfig').default;
}

export const loadDataAsProps = ({
  store: { dispatch },
  ownProps: { locale },
}) => ({
  ...global.BROWSER
  && customLocales.includes(locale) && {
    customIntlData: () => dispatch(queryCustomIntlData(locale)),
  },
  MycaVoc: () => dispatch(queryModule('axp-voice-of-customer')),
});

loadDataAsProps.ssr = true;

Container.holocron = {
  name: moduleName,
  reducer,
  loadModuleData,
  options: { provideModuleState: false },
};

const hocChain = compose(
  oneAppModuleWrapper(moduleName),
  connectAsync({ loadDataAsProps }),
  withRouter,
  withDayJSLocale
);

export default hocChain(Container);

Container.propTypes = {
  MycaVoc: func.isRequired,
  children: node.isRequired,
  languageData: shape({
    titleTemplate: string,
    defaultTitle: string,
  }).isRequired,
  location: shape({
    query: shape({
      ismobile: string,
      vortex: string,
    }),
    pathname: string,
  }).isRequired,
  isLoading: func.isRequired,
  loadedWithErrors: func.isRequired,
  routes: arrayOf(
    shape({
      title: string,
      getTitle: func,
    })
  ).isRequired,
};
